import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

/*
 * DS Checkbox (shadcn-inspired pattern, adapted to the project's no-Radix
 * stack). A real <input type="checkbox"> sits on top of the visual span at
 * Opacity 0 — it owns all the accessibility (focus, keyboard, screen reader)
 * And forwards events natively. The visual <span> mirrors the input state via
 * Data-checked / data-indeterminate attributes (see @utility ds-checkbox).
 *
 * Indeterminate is forwarded to the underlying input through a fused ref so
 * Consumers (e.g. select-all headers) can also read it back if needed.
 *
 * For consumers where the visual must live inside another interactive parent
 * (e.g. a <button aria-pressed>), use the @utility ds-checkbox directly on a
 * <span data-checked> — bypassing this component avoids nesting two
 * Interactive controls.
 */

const WRAPPER_CLASS = 'tw:relative tw:inline-flex tw:items-center'
const INPUT_CLASS = 'tw:peer tw:absolute tw:inset-0 tw:w-full tw:h-full '
  + 'tw:opacity-0 tw:cursor-pointer tw:disabled:cursor-not-allowed tw:m-0'
// Pointer-events:none on the visual lets clicks fall through to the input below.
// Without it, the visual span's position:relative (from ds-checkbox) creates a
// Stacking context that paints over the input, swallowing the click.
const VISUAL_CLASS = 'tw:ds-checkbox! tw:pointer-events-none '
  + 'tw:peer-focus-visible:ring-2 tw:peer-focus-visible:ring-ds-primary '
  + 'tw:peer-disabled:opacity-50'

const Checkbox = forwardRef(({
  checked = false,
  className = '',
  disabled = false,
  indeterminate = false,
  onChange,
  ...rest
}, ref) => {
  const internalRef = useRef(null)

  const setRef = useCallback(node => {
    internalRef.current = node
    if (typeof ref === 'function')
      ref(node)
    else if (ref)
      ref.current = node
  }, [ref])

  useEffect(() => {
    if (internalRef.current)
      internalRef.current.indeterminate = indeterminate
  }, [indeterminate])

  const visualChecked = checked && !indeterminate ? 'true' : 'false'

  return (
    <span className={`${WRAPPER_CLASS} ${className}`.trim()}>
      <input
        ref={setRef}
        checked={checked}
        className={INPUT_CLASS}
        disabled={disabled}
        type="checkbox"
        onChange={onChange}
        {...rest}
      />
      <span
        aria-hidden="true"
        className={VISUAL_CLASS}
        data-checked={visualChecked}
        data-indeterminate={indeterminate ? 'true' : 'false'}
      />
    </span>
  )
})

Checkbox.displayName = 'Checkbox'

Checkbox.propTypes = {
  checked: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func
}

Checkbox.defaultProps = {
  checked: false,
  className: '',
  disabled: false,
  indeterminate: false,
  onChange: () => null
}

export default Checkbox
